import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Avatar,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Person as UserIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { PageHeader } from '../../components/common';
import { adminApi } from '../../utils/api';

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const { data } = await adminApi.getUser(userId);
      setUser(data);
      setSelectedRole(data.user_role);
    } catch (err) {
      console.error('Failed to fetch user:', err);
      // Show empty state on error - no mock data
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleUpdate = async () => {
    try {
      await adminApi.updateUserRole(userId, selectedRole);
      setUser((prev) => ({ ...prev, user_role: selectedRole }));
      setRoleDialogOpen(false);
    } catch (err) {
      console.error('Failed to update role:', err);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await adminApi.toggleUserStatus(userId, !user.disabled);
      setUser((prev) => ({ ...prev, disabled: !prev.disabled }));
    } catch (err) {
      console.error('Failed to toggle status:', err);
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton width={200} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="user details"
        breadcrumbs={[{ label: 'users', path: '/users' }, { label: user?.email || 'user' }]}
        action={
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/users')}
          >
            Back to Users
          </Button>
        }
      />

      <Grid container spacing={3}>
        {/* User Info Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    fontSize: '2rem',
                    backgroundColor: user?.user_role === 'admin'
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                  }}
                >
                  {user?.email?.[0]?.toUpperCase()}
                </Avatar>
                <Typography variant="h4" sx={{ mb: 0.5 }}>
                  {user?.email?.split('@')[0]}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                  {user?.email}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                  <Chip
                    icon={user?.user_role === 'admin' ? <AdminIcon /> : <UserIcon />}
                    label={user?.user_role}
                    sx={{
                      backgroundColor:
                        user?.user_role === 'admin'
                          ? `${theme.palette.primary.main}20`
                          : `${theme.palette.text.secondary}15`,
                      color:
                        user?.user_role === 'admin'
                          ? theme.palette.primary.main
                          : theme.palette.text.primary,
                    }}
                  />
                  <Chip
                    icon={user?.disabled ? <BlockIcon /> : <ActiveIcon />}
                    label={user?.disabled ? 'Disabled' : 'Active'}
                    sx={{
                      backgroundColor: user?.disabled
                        ? `${theme.palette.error.main}20`
                        : `${theme.palette.success.main}20`,
                      color: user?.disabled
                        ? theme.palette.error.main
                        : theme.palette.success.main,
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Stats */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                      {user?.recipes_count || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      Recipes
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                      {user?.lists_count || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      Lists
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Actions */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setRoleDialogOpen(true)}
                >
                  Change Role
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  color={user?.disabled ? 'success' : 'error'}
                  onClick={handleToggleStatus}
                >
                  {user?.disabled ? 'Enable Account' : 'Disable Account'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Details */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Account Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    User ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {user?.id}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {user?.created_at
                      ? format(new Date(user.created_at), 'PPpp')
                      : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Last Sign In
                  </Typography>
                  <Typography variant="body2">
                    {user?.last_sign_in_at
                      ? format(new Date(user.last_sign_in_at), 'PPpp')
                      : 'Never'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Recent Recipes */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Recent Recipes
              </Typography>
              {user?.recent_recipes?.length > 0 ? (
                <List disablePadding>
                  {user.recent_recipes.map((recipe, index) => (
                    <ListItem
                      key={recipe.id}
                      disablePadding
                      sx={{
                        py: 1.5,
                        borderBottom:
                          index < user.recent_recipes.length - 1
                            ? `1px solid ${theme.palette.divider}`
                            : 'none',
                      }}
                    >
                      <ListItemText
                        primary={recipe.title}
                        secondary={format(new Date(recipe.created_at), 'MMM d, yyyy')}
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  No recipes yet
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Households */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Households
              </Typography>
              {user?.households?.length > 0 ? (
                <List disablePadding>
                  {user.households.map((household) => (
                    <ListItem key={household.id} disablePadding sx={{ py: 1 }}>
                      <ListItemText
                        primary={household.name}
                        secondary={`Role: ${household.role}`}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Not a member of any household
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Role Change Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change User Role</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              label="Role"
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRoleUpdate}>
            Update Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetail;
